import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Menu } from 'primeng/menu';
import { MenuItem, MessageService, ToastMessageOptions } from 'primeng/api';
import { RouterModule, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { MenuModule } from 'primeng/menu';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AuthService, TokenSource } from '../services/auth.service';
import { LayoutService } from '../services/layout.service';
import { LanguageService } from '../services/language.service';
import { SpaceService } from '../services/space.service';
import { ThemeService, ThemeMode } from '../services/theme.service';
import { Subscription } from '../services/subscription';
import { Signalr } from '../services/signalr';
import { UsersService } from '@generated/services/users.service';
import { environment } from '@env/environment';
import { AssistantPanelComponent } from '@shared/components/assistant-panel/assistant-panel.component';
import { AssistantUiService } from '@core/assistant/assistant-ui.service';
import { SpaceDto } from '@generated/models/space-dto';
import posthog from 'posthog-js';

/** Пункт основной навигации. `primary` — попадает в нижний таб-бар
 *  мобильной версии, остальные живут в «Ещё». */
interface NavItem {
  label: string;
  icon: string;
  routerLink?: string;
  externalUrl?: string;
  primary?: boolean;
  warningIcon?: string;
  warningTooltip?: string;
}

@Component({
  selector: 'app-layout',
  imports: [
    RouterModule,
    ButtonModule,
    DrawerModule,
    MenuModule,
    ToastModule,
    TooltipModule,
    ProgressSpinnerModule,
    AssistantPanelComponent
  ],
  templateUrl: './layout.html',
  styleUrl: './layout.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class Layout {
  private readonly messageService = inject(MessageService);
  private readonly authService = inject(AuthService);
  private readonly layoutService = inject(LayoutService);
  private readonly signalrService = inject(Signalr);
  private readonly usersService = inject(UsersService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  private readonly assistantUi = inject(AssistantUiService);

  readonly languageService = inject(LanguageService);
  readonly spaceService = inject(SpaceService);
  readonly subscriptionService = inject(Subscription);
  readonly themeService = inject(ThemeService);

  readonly sidebarVisible = signal(false);
  readonly profileLoading = signal(true);
  readonly profileError = signal(false);
  readonly signedInAsEmail = signal<string | null>(null);
  readonly isAdmin = signal(false);
  readonly canShowAssistant = signal(false);

  readonly sidebarCollapsed = this.layoutService.sidebarCollapsed;

  /** Открыт ли режим агента — от него зависит ширина основной зоны. */
  readonly assistantOpen = this.assistantUi.panelOpen.asReadonly();
  /** Агент ждёт решения — подсвечиваем кнопку, даже если панель свернули. */
  readonly assistantPending = this.assistantUi.hasPending;

  readonly askAgentLabel = $localize`:@@assistant.ask:Ask agent`;
  readonly selectWorkspaceLabel = $localize`:@@workspace.select:Select workspace`;
  readonly logoutLabel = $localize`:@@common.logout:Logout`;
  readonly moreLabel = $localize`:@@nav.more:More`;
  readonly appName = environment.appName;

  private readonly spaceMenu = viewChild.required<Menu>('spaceMenu');
  private readonly mobileSpaceMenu = viewChild.required<Menu>('mobileSpaceMenu');
  private readonly languageMenu = viewChild.required<Menu>('languageMenu');
  private readonly mobileLanguageMenu = viewChild.required<Menu>('mobileLanguageMenu');

  readonly spaceMenuItems = computed<MenuItem[]>(() => {
    const userId = this.authService.getUserProfile()?.id;
    const spaces = this.spaceService.spaces();
    const currentId = this.spaceService.currentSpace()?.id;
    const mine = spaces.filter(s => s.owner_id === userId);
    const shared = spaces.filter(s => s.owner_id !== userId);

    const toItem = (s: SpaceDto): MenuItem => ({
      id: s.id,
      label: s.name || 'Unnamed',
      icon: s.id === currentId ? 'pi pi-check' : (s.is_default ? 'pi pi-star-fill' : 'pi pi-briefcase'),
      command: () => {
        if (s.id !== currentId) {
          const onSpaceEdit = /^\/spaces\/[^/]+$/.test(this.router.url);
          if (onSpaceEdit) {
            this.router.navigate(['/spaces', s.id]).then(() => {
              this.spaceService.setCurrentSpace(s);
            });
          } else {
            this.spaceService.setCurrentSpace(s);
          }
        }
      }
    });

    const items: MenuItem[] = [];
    if (mine.length) {
      const myItems = mine.map(toItem);
      myItems.push({
        label: $localize`:@@workspace.new:New workspace`,
        icon: 'pi pi-plus',
        command: () => this.router.navigate(['/spaces/new'])
      });
      items.push({ label: $localize`:@@workspace.mine:My workspaces`, items: myItems });
    }
    if (shared.length) {
      const byOwner = new Map<string, SpaceDto[]>();
      for (const s of shared) {
        const key = s.owner_email || 'Unknown';
        if (!byOwner.has(key)) byOwner.set(key, []);
        byOwner.get(key)!.push(s);
      }
      items.push({ separator: true });
      items.push({ label: $localize`:@@workspace.shared:Shared with me`, disabled: true });
      for (const [email, ownerSpaces] of byOwner) {
        items.push({ label: email, items: ownerSpaces.map(toItem) });
      }
    }
    return items;
  });

  private readonly baseMenuItems: NavItem[] = [
    {
      label: $localize`:@@nav.agents:Agents`,
      icon: 'pi pi-microchip',
      routerLink: '/agents',
      primary: true
    },
    {
      label: $localize`:@@nav.conversations:Conversations`,
      icon: 'pi pi-history',
      routerLink: '/conversations',
      primary: true
    },
    {
      label: $localize`:@@nav.analytics:Analytics`,
      icon: 'pi pi-chart-bar',
      routerLink: '/analytics',
      primary: true
    },
    {
      label: $localize`:@@nav.tools:Tools`,
      icon: 'pi pi-wrench',
      routerLink: '/tools',
      primary: true
    },
    {
      label: $localize`:@@nav.tests:Tests`,
      icon: 'pi pi-flask',
      routerLink: '/tests',
      primary: true
    },
    {
      label: $localize`:@@nav.phoneNumbers:Phone Numbers`,
      icon: 'pi pi-phone',
      routerLink: '/phone-numbers'
    },
    {
      label: $localize`:@@nav.outbound:Outbound`,
      icon: 'pi pi-send',
      routerLink: '/outbound-campaigns'
    },
    {
      label: $localize`:@@nav.developers:Developers`,
      icon: 'pi pi-code',
      routerLink: '/developers'
    },
    {
      label: $localize`:@@nav.billing:Billing`,
      icon: 'pi pi-credit-card',
      routerLink: '/billing',
      warningIcon: 'pi pi-exclamation-triangle',
      warningTooltip: $localize`:@@nav.billing.warning:You have invoices requiring attention`
    },
    {
      label: $localize`:@@nav.settings:Settings`,
      icon: 'pi pi-cog',
      routerLink: '/settings'
    },
    {
      label: $localize`:@@nav.docs:Documentation`,
      icon: 'pi pi-book',
      externalUrl: environment.docsUrl
    }
  ];

  /**
   * Ссылки на разделы документации в верхней полосе.
   *
   * <p>Язык в адрес не подставляем: страницы лежат под префиксом локали, но
   * доки сами редиректят <code>/guide/cli</code> на нужную по
   * <code>Accept-Language</code>. Ссылка получается короче и не ломается,
   * если перевода страницы нет.</p>
   *
   * <p>Домен берём из окружения — он разный у инстансов.</p>
   *
   * <p>MCP и CLI — два способа управлять платформой не руками, и оба человеку
   * неоткуда узнать, пока о них не сказали. Поэтому не в подвале справки, а
   * на виду.</p>
   */
  readonly docsLinks = (() => {
    const base = environment.docsUrl.replace(/\/$/, '');

    // Названия не переводим: это имена технологий, а не слова.
    return [
      { label: 'MCP', page: 'mcp' },
      { label: 'CLI', page: 'cli' }
    ].map(item => ({ ...item, url: `${base}/guide/${item.page}` }));
  })();

  /** Админский пункт добавляется по роли из ОРИГИНАЛЬНОГО токена —
   *  админ сохраняет админское меню, пока «входит как» другой юзер. */
  readonly menuItems = computed<NavItem[]>(() => {
    if (!this.isAdmin()) return this.baseMenuItems;
    return [
      ...this.baseMenuItems,
      { label: $localize`:@@nav.admin:Admin`, icon: 'pi pi-shield', routerLink: '/admin' }
    ];
  });

  /** Нижний таб-бар мобильной версии: 4 основных раздела + «Ещё». */
  readonly primaryMenuItems = computed<NavItem[]>(() => this.menuItems().filter(i => i.primary));

  /** Всё, что не влезло в таб-бар — уходит в drawer «Ещё». */
  readonly secondaryMenuItems = computed<NavItem[]>(() => this.menuItems().filter(i => !i.primary));

  readonly languageMenuItems = computed<MenuItem[]>(() => {
    const current = this.languageService.currentLang();
    return this.languageService.languages.map(l => ({
      label: `${l.flag} ${l.label}`,
      icon: l.code === current ? 'pi pi-check' : undefined,
      command: () => this.languageService.setLang(l.code)
    }));
  });

  readonly currentLanguageLabel = computed(() => this.languageService.getCurrentInfo().label);

  readonly themeOptions: { mode: ThemeMode; icon: string; label: string }[] = [
    { mode: 'light', icon: 'pi pi-sun', label: $localize`:@@theme.light:Light` },
    { mode: 'dark', icon: 'pi pi-moon', label: $localize`:@@theme.dark:Dark` },
    { mode: 'system', icon: 'pi pi-desktop', label: $localize`:@@theme.system:System` }
  ];

  readonly themeLabel = computed(() =>
    this.themeService.isDark()
      ? $localize`:@@theme.switchToLight:Switch to light theme`
      : $localize`:@@theme.switchToDark:Switch to dark theme`
  );

  constructor() {
    this.loadProfile();
  }

  clearMessage(_message: ToastMessageOptions): void {
    this.messageService.clear();
  }

  loadProfile(): void {
    this.profileLoading.set(true);
    this.profileError.set(false);
    this.usersService.apiUsersMeGet()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.profileLoading.set(false);
          this.initApp();
        },
        error: () => {
          this.profileLoading.set(false);
          this.profileError.set(true);
        }
      });
  }

  private initApp(): void {
    this.checkSignedInStatus();
    this.checkAdminStatus();
    this.checkAssistantAccess();
    this.spaceService.loadSpaces();
    this.subscriptionService.loadSubscription();
    void this.signalrService.start();
    this.identifyUser();
  }

  private checkSignedInStatus(): void {
    const token = localStorage.getItem('signed-in-as-user-token');
    if (token) {
      this.signedInAsEmail.set(this.getEmailFromToken(token));
    }
  }

  private checkAdminStatus(): void {
    // Original token — admin keeps admin UI while impersonating another user.
    this.isAdmin.set(this.authService.hasClientRole('admin', TokenSource.Original));
  }

  private checkAssistantAccess(): void {
    // Hard kill-switch from env wins over everything. Default true so
    // legacy environment files without the field keep their behaviour.
    if (environment.assistantEnabled === false) {
      this.canShowAssistant.set(false);
      return;
    }
    // Defensive: ignore blank entries so '', '""', whitespace → "show to all".
    const roles = (environment.assistantRoles ?? [])
      .map(r => String(r).trim())
      .filter(Boolean);
    // No roles configured → assistant chat is visible to everyone.
    this.canShowAssistant.set(
      roles.length === 0 ? true : this.authService.hasAnyClientRole(roles, TokenSource.Original)
    );
  }

  navigateToAdminSignIn(): void {
    this.router.navigate(['/admin/users']);
  }

  goToSpaceSettings(spaceId: string, event: Event): void {
    event.stopPropagation();
    this.spaceMenu().hide();
    this.mobileSpaceMenu().hide();
    this.sidebarVisible.set(false);
    this.router.navigate(['/spaces', spaceId]);
  }

  toggleSpaceMenu(event: Event): void {
    this.spaceMenu().toggle(event);
  }

  toggleMobileSpaceMenu(event: Event): void {
    this.mobileSpaceMenu().toggle(event);
  }

  toggleLanguageMenu(event: Event): void {
    this.languageMenu().toggle(event);
  }

  toggleMobileLanguageMenu(event: Event): void {
    this.mobileLanguageMenu().toggle(event);
  }

  toggleSidebar(): void {
    this.sidebarVisible.update(v => !v);
  }

  closeSidebar(): void {
    this.sidebarVisible.set(false);
  }

  toggleAssistant(): void {
    this.assistantUi.togglePanel();
  }

  toggleCollapse(): void {
    this.layoutService.toggleSidebarCollapsed();
  }

  setTheme(mode: ThemeMode): void {
    this.themeService.set(mode);
  }

  toggleTheme(): void {
    this.themeService.toggle();
  }

  logout(): void {
    localStorage.removeItem('signed-in-as-user-token');
    this.authService.logout();
  }

  private getEmailFromToken(token: string): string | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const decoded = JSON.parse(jsonPayload);
      return decoded.email || decoded.sub || null;
    } catch {
      return null;
    }
  }

  private identifyUser(): void {
    const userProfile = this.authService.getUserProfile();
    if (!userProfile?.id) return;
    if (!environment.posthog.key || !environment.posthog.host) return;

    posthog.identify(userProfile.id, {
      email: userProfile.email,
      name: userProfile.name,
      is_admin: this.isAdmin(),
      signed_in_as: this.signedInAsEmail()
    });
  }
}
